/*
 * Dentis – influencer-aware Klaviyo sign-up forms.
 *
 * Reads the influencer discount code from the landing URL (e.g.
 * ?discount_codes=EMMA20), derives the first name, and swaps author-defined
 * tokens inside any Klaviyo sign-up form when it opens.
 *
 * Tokens you can type into the Klaviyo form copy (text/heading blocks):
 *   [[name]]            -> influencer first name (e.g. "Emma")
 *   [[name|venn]]       -> same, but renders "venn" when there is no name
 *   [[code]]            -> full discount code as-is (e.g. "EMMA20")
 *   [[code|RABATT]]     -> full code, with a fallback when no code is present
 *
 * Names come from theme settings (Influencer popup), rendered as JSON in
 * #DentisInfluencerNames by layout/theme.liquid:
 *   names    "STEM=Name" per line; "STEM=" alone means show no name. A full
 *            code may also be used as the key.
 *   endings  code endings stripped to find the stem, e.g. "BLACK26, BLACK20, 20".
 * A code whose stem is not in the table is title-cased (EMMABLACK26 -> "Emma").
 *
 * Capture (optional): give a hidden field in the form the CSS class
 * "js-influencer-code" and its value is set to the code on open.
 */
(function () {
  'use strict';

  var PARAM_KEYS = ['discount_code', 'discount_codes', 'utm_content', 'discount'];
  var STORAGE_KEY = 'dentis_influencer_code';
  var HIDDEN_FIELD_SELECTOR = '.js-influencer-code, [data-influencer-code]';
  var DEFAULT_NAME = 'venn';
  var DEFAULT_ENDINGS = ['20'];

  function readConfig() {
    var names = {};
    var endings = DEFAULT_ENDINGS;
    var raw = {};
    try {
      var el = document.getElementById('DentisInfluencerNames');
      if (el) raw = JSON.parse(el.textContent) || {};
    } catch (e) {}

    String(raw.names || '').split(/\r?\n/).forEach(function (line) {
      var i = line.indexOf('=');
      if (i < 1) return;
      var key = line.slice(0, i).replace(/\s+/g, '').toUpperCase();
      if (key) names[key] = line.slice(i + 1).trim();
    });

    var parsed = String(raw.endings || '').split(/[,\n]/).map(function (s) {
      return s.replace(/\s+/g, '').toUpperCase();
    }).filter(Boolean);
    if (parsed.length) endings = parsed;
    endings = endings.slice().sort(function (a, b) { return b.length - a.length; });

    return { names: names, endings: endings };
  }

  var CONFIG = readConfig();

  function readCodeFromUrl() {
    var params = new URLSearchParams(window.location.search);
    for (var i = 0; i < PARAM_KEYS.length; i++) {
      var raw = params.get(PARAM_KEYS[i]);
      if (raw) return raw.split(',')[0].trim();
    }
    return '';
  }

  function getCode() {
    var fromUrl = readCodeFromUrl();
    if (fromUrl) {
      try { sessionStorage.setItem(STORAGE_KEY, fromUrl); } catch (e) {}
      return fromUrl;
    }
    try { return sessionStorage.getItem(STORAGE_KEY) || ''; } catch (e) { return ''; }
  }

  function stemOf(code) {
    var stem = code.replace(/\s+/g, '').toUpperCase();
    for (var i = 0; i < CONFIG.endings.length; i++) {
      var ending = CONFIG.endings[i];
      if (stem.length > ending.length && stem.slice(-ending.length) === ending) {
        stem = stem.slice(0, -ending.length);
        break;
      }
    }
    return stem.replace(/[0-9]+$/, '');
  }

  function has(map, key) {
    return Object.prototype.hasOwnProperty.call(map, key);
  }

  function toFirstName(code) {
    if (!code) return '';
    var full = code.replace(/\s+/g, '').toUpperCase();
    if (has(CONFIG.names, full)) return CONFIG.names[full];
    var stem = stemOf(code);
    if (has(CONFIG.names, stem)) return CONFIG.names[stem];
    var name = stem.replace(/[-_]+/g, ' ').trim();
    if (!name) return '';
    return name.toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }
  function buildValues(code) {
    return {
      name: toFirstName(code),
      code: code
    };
  }

  function resolveToken(key, fallback, values) {
    var value = values[key];
    if (value) return value;
    if (typeof fallback === 'string') return fallback;
    return key === 'name' ? DEFAULT_NAME : '';
  }

  function replaceTokens(text, values) {
    return text.replace(/\[\[\s*(name|code)\s*(?:\|\s*([^\]]*))?\]\]/gi, function (_match, key, fallback) {
      return resolveToken(key.toLowerCase(), fallback != null ? fallback.trim() : null, values);
    });
  }

  function applyToText(root, values) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    var node;
    var pending = [];
    while ((node = walker.nextNode())) {
      if (/\[\[\s*(name|code)/i.test(node.nodeValue)) pending.push(node);
    }
    pending.forEach(function (textNode) {
      textNode.nodeValue = replaceTokens(textNode.nodeValue, values);
    });
  }

  function applyToHiddenFields(root, code) {
    if (!code) return;
    var fields = root.querySelectorAll(HIDDEN_FIELD_SELECTOR);
    Array.prototype.forEach.call(fields, function (field) {
      field.value = code;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function applyToForm(root) {
    if (!root) return;
    var code = getCode();
    var values = buildValues(code);
    applyToText(root, values);
    applyToHiddenFields(root, code);
  }

  function applyToAllForms() {
    var forms = document.querySelectorAll('[class*="klaviyo-form-"]');
    if (!forms.length) return;
    var code = getCode();
    var values = buildValues(code);
    Array.prototype.forEach.call(forms, function (form) {
      applyToText(form, values);
      applyToHiddenFields(form, code);
    });
  }

  function start() {
    getCode();
    applyToAllForms();

    // Fast path: Klaviyo dispatches this when a form opens.
    window.addEventListener('klaviyoForms', function (e) {
      if (e && e.detail && (e.detail.type === 'open' || e.detail.type === 'embedOpen')) {
        applyToAllForms();
      }
    });

    // Robust path: forms render asynchronously (and may render before our
    // listener attaches), so watch the DOM and re-apply when tokens appear.
    if (window.MutationObserver && document.body) {
      var observer = new MutationObserver(function () {
        applyToAllForms();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
